import { useMutation, useQueryClient } from "@tanstack/react-query";
import _ from "lodash";
import { configuration } from "src/configuration";
import {
  FetchError,
  optimisticallyUpdateQueryData,
  queryKeys,
  useAccessToken,
  type EditIssueCollaboratorsDto,
  type GetIssuesDto,
  type IssueDto,
  type UserDto,
} from "src/http";

type Variables = {
  issueGuid: string;
  collaborators: UserDto[];
  teamSlug?: string;
};

const useEditIssueCollaboratorsRequest = () => {
  const { getAccessToken } = useAccessToken();

  async function editIssueCollaboratorsRequest(variables: Variables) {
    const accessToken = await getAccessToken();
    const body: EditIssueCollaboratorsDto = {
      collaboratorsUserIds: variables.collaborators.map((x) => x.userId),
    };

    const response = await fetch(`${configuration.apiRootUrl}/issues/${variables.issueGuid}/collaborators`, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
      method: "put",
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      throw new FetchError(response);
    }
  }

  return { editIssueCollaboratorsRequest };
};

const useEditIssueCollaborators = () => {
  const { editIssueCollaboratorsRequest } = useEditIssueCollaboratorsRequest();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: editIssueCollaboratorsRequest,
    onMutate: async (variables) => {
      const cachedUserData: { [userGuid: string]: GetIssuesDto | undefined } = {};
      let cachedTeamData: GetIssuesDto | undefined;

      const cachedDetailData = await optimisticallyUpdateQueryData<IssueDto>(
        queryClient,
        queryKeys.issues.getIssueDetail(variables.issueGuid),
        (cachedData) => ({
          ...cachedData,
          collaborators: variables.collaborators,
        }),
      );

      const oldCollaborators = cachedDetailData?.collaborators ?? [];
      const collaboratorsToRemove = _.differenceBy(oldCollaborators, variables.collaborators, (x) => x.userId);
      const collaboratorsToUpdate = _.intersectionBy(oldCollaborators, variables.collaborators, (x) => x.userId);

      collaboratorsToRemove.forEach(async (collaborator) => {
        cachedUserData[collaborator.userId] = await optimisticallyUpdateQueryData<GetIssuesDto>(
          queryClient,
          queryKeys.issues.getIssues(undefined, collaborator.userId),
          (cachedData) => ({
            issues: cachedData.issues.filter((issue) => issue.guid !== variables.issueGuid),
          }),
        );
      });

      collaboratorsToUpdate.forEach(async (collaborator) => {
        cachedUserData[collaborator.userId] = await optimisticallyUpdateQueryData<GetIssuesDto>(
          queryClient,
          queryKeys.issues.getIssues(undefined, collaborator.userId),
          (cachedData) => ({
            issues: cachedData.issues.map((issue) => {
              if (issue.guid === variables.issueGuid) {
                return {
                  ...issue,
                  collaborators: variables.collaborators,
                };
              } else {
                return issue;
              }
            }),
          }),
        );
      });

      if (cachedDetailData?.assignedTeam != null) {
        cachedTeamData = await optimisticallyUpdateQueryData<GetIssuesDto>(
          queryClient,
          queryKeys.issues.getIssues(cachedDetailData.assignedTeam.slug, undefined),
          (cachedData) => ({
            issues: cachedData.issues.map((issue) => {
              if (issue.guid === variables.issueGuid) {
                return {
                  ...issue,
                  collaborators: variables.collaborators,
                };
              } else {
                return issue;
              }
            }),
          }),
        );
      }

      return {
        cachedDetailData,
        cachedUserData,
        cachedTeamData,
      };
    },
    onSettled: (_data, _error, variables) => {
      queryClient.invalidateQueries(queryKeys.issues.getIssueDetail(variables.issueGuid));
      queryClient.invalidateQueries(queryKeys.issues.getIssueFeed(variables.issueGuid));

      if (variables.teamSlug) {
        queryClient.invalidateQueries(queryKeys.issues.getIssues(variables.teamSlug, undefined));
      }

      variables.collaborators.forEach((collaborator) => {
        queryClient.invalidateQueries(queryKeys.issues.getIssues(undefined, collaborator.userId));
      });
    },
  });
};

export { useEditIssueCollaborators };
